import { useState } from "react";
import styled, { createGlobalStyle, keyframes, css } from "styled-components";
import { FaFlag } from "react-icons/fa";
import logo from "../assets/logo.PNG";
import p1 from "../assets/p1.jpeg";
import p2 from "../assets/p2.jpeg";
import p3 from "../assets/p3.jpeg";
import p4 from "../assets/p4.jpeg";
import p5 from "../assets/p5.jpeg";
import p6 from "../assets/p6.jpeg";
import p7 from "../assets/p7.jpeg";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { width: 100%; height: 100%; overflow: hidden; user-select: none; }
`;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const fadeUp = keyframes`from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}`;
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const scaleIn = keyframes`from{opacity:0;transform:scale(0.93)}to{opacity:1;transform:scale(1)}`;
const countIn = keyframes`from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}`;
const shimmer = keyframes`0%{background-position:200% 0}100%{background-position:-200% 0}`;

const modalSlideUp = keyframes`
  from { transform: translateX(-50%) translateY(100%); opacity: 0; }
  to   { transform: translateX(-50%) translateY(0);    opacity: 1; }
`;
const modalSlideDown = keyframes`
  from { transform: translateX(-50%) translateY(0);    opacity: 1; }
  to   { transform: translateX(-50%) translateY(100%); opacity: 0; }
`;
const backdropIn = keyframes`from{opacity:0}to{opacity:1}`;
const goldGlow = keyframes`0%,100%{box-shadow:0 0 40px rgba(212,160,23,0.35),0 0 80px rgba(212,160,23,0.12),0 0 0 1px rgba(212,160,23,0.28)}50%{box-shadow:0 0 65px rgba(212,160,23,0.6),0 0 120px rgba(212,160,23,0.22),0 0 0 1px rgba(212,160,23,0.48)}`;
const flareIconPulse = keyframes`0%,100%{transform:scale(1);filter:drop-shadow(0 0 8px rgba(212,160,23,0.5))}50%{transform:scale(1.15);filter:drop-shadow(0 0 22px rgba(212,160,23,0.95))}`;

// ─── Screen / Shell ───────────────────────────────────────────────────────────

const Screen = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  max-width: 430px;
  margin: 0 auto;
  background: linear-gradient(175deg, #7404be 0%, #a855fd 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(
        ellipse 80% 50% at 50% -10%,
        #7404be55 0%,
        transparent 65%
      ),
      radial-gradient(
        ellipse 55% 35% at 15% 100%,
        #a855fd22 0%,
        transparent 60%
      );
  }
`;

// ─── Top Bar ─────────────────────────────────────────────────────────────────

const TopBar = styled.div`
  position: relative;
  z-index: 10;
  width: 100%;
  padding: 54px 24px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;
const LogoImg = styled.img`
  width: 40px;
  opacity: 0.9;
`;
const TopTitle = styled.h1`
  font-family: "Mendl", sans-serif;
  font-size: 28px;
  font-weight: 300;
  font-style: italic;
  color: #fff;
  letter-spacing: 0.04em;
`;
const TopRight = styled.div`
  width: 36px;
`; /* spacer to keep title centred */

// ─── Tab Bar ──────────────────────────────────────────────────────────────────

const TabWrap = styled.div`
  position: relative;
  z-index: 10;
  flex-shrink: 0;
  padding: 18px 20px 0;
  display: flex;
  gap: 8px;
`;
const TabBtn = styled.button`
  height: 34px;
  border-radius: 100px;
  padding: 0 15px;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.12)"};
  background: ${({ $active }) =>
    $active ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.05)"};
  backdrop-filter: blur(12px);
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? "#fff" : "rgba(255,255,255,0.45)")};
  letter-spacing: 0.03em;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  box-shadow: ${({ $active }) =>
    $active
      ? "0 2px 12px rgba(0,0,0,0.25), 0 1px 0 rgba(255,255,255,0.2) inset"
      : "none"};
`;

// ─── Scrollable Body ──────────────────────────────────────────────────────────

const Body = styled.div`
  position: relative;
  z-index: 5;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px 20px 12px;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
`;

// ─── Section label ────────────────────────────────────────────────────────────

const SectionLabel = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 10px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.45);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 12px;
  padding-left: 2px;
  animation: ${fadeUp} 0.3s ease both;
`;

const SectionGap = styled.div`
  height: 24px;
`;

// ─── Match Grid ───────────────────────────────────────────────────────────────

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  animation: ${fadeUp} 0.35s ease both;
`;

const MatchCard = styled.div`
  border-radius: 22px;
  overflow: hidden;
  position: relative;
  aspect-ratio: 3/4;
  cursor: pointer;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(255, 255, 255, 0.08) inset;
  transition: transform 0.18s cubic-bezier(0.23, 1, 0.32, 1);
  &:active {
    transform: scale(0.95);
  }
`;
const MatchImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  pointer-events: none;
`;
const MatchGrad = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 35%,
    rgba(0, 0, 0, 0.35) 65%,
    rgba(0, 0, 0, 0.88) 100%
  );
`;
const MatchInfo = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px 13px 13px;
`;
const MatchName = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #fff;
  letter-spacing: 0.02em;
  line-height: 1.2;
`;
const MatchAge = styled.span`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.65);
  margin-left: 5px;
`;
const MatchHandle = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.4);
  letter-spacing: 0.02em;
  margin-top: 1px;
`;

/* Online dot */
const OnlineDot = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #4ade80;
  border: 2px solid rgba(0, 0, 0, 0.4);
  box-shadow: 0 0 6px rgba(74, 222, 128, 0.7);
`;

/* "New" badge */
const NewBadge = styled.div`
  position: absolute;
  top: 11px;
  left: 11px;
  height: 20px;
  border-radius: 100px;
  padding: 0 9px;
  background: rgba(168, 85, 253, 0.88);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.22);
  display: flex;
  align-items: center;
  font-family: "Mendl", sans-serif;
  font-size: 9px;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  box-shadow: 0 2px 8px rgba(168, 85, 253, 0.5);
`;

/* Flare badge */
const FlareBadge = styled.div`
  position: absolute;
  top: 11px;
  left: 11px;
  height: 22px;
  border-radius: 100px;
  padding: 0 9px;
  background: rgba(249, 168, 37, 0.88);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: "Mendl", sans-serif;
  font-size: 9px;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  box-shadow: 0 2px 8px rgba(249, 168, 37, 0.5);
`;

// ─── Empty State ──────────────────────────────────────────────────────────────

const EmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 28px 28px;
  gap: 14px;
  text-align: center;
  animation: ${scaleIn} 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
`;
const EmptyCircle = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.14);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
  margin-bottom: 4px;
  box-shadow: 0 0 30px rgba(168, 85, 253, 0.2);
`;
const EmptyTitle = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 20px;
  font-weight: 400;
  font-style: italic;
  color: rgba(255, 255, 255, 0.8);
`;
const EmptySub = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.35);
  line-height: 1.65;
  max-width: 260px;
`;

// ─── Divider ─────────────────────────────────────────────────────────────────

const Divider = styled.div`
  height: 1px;
  margin: 4px 0 20px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.1),
    transparent
  );
`;

// ─── Profile Modal ────────────────────────────────────────────────────────────

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(8px);
  animation: ${backdropIn} 0.28s ease both;
`;
const ModalSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 430px;
  height: 92vh;
  border-radius: 28px 28px 0 0;
  background: #0d0018;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 301;
  -webkit-overflow-scrolling: touch;
  animation: ${({ $closing }) => ($closing ? modalSlideDown : modalSlideUp)}
    0.38s cubic-bezier(0.16, 1, 0.3, 1) both;
  box-shadow:
    0 -8px 40px rgba(116, 4, 190, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.08);
  &::-webkit-scrollbar {
    display: none;
  }
`;
const ModalPhotoWrap = styled.div`
  position: relative;
  width: 90%;
  margin: 20px auto 0;
  border-radius: 20px;
  overflow: hidden;
`;
const ModalPhoto = styled.img`
  width: 100%;
  height: auto;
  display: block;
  pointer-events: none;
`;
const ModalPhotoGrad = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 50%,
    rgba(0, 0, 0, 0.72) 100%
  );
`;
const ModalFlagWrap = styled.div`
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 4;
  display: inline-flex;
`;
const ModalFlagPill = styled.div`
  height: 28px;
  border-radius: 100px;
  padding: 0 10px;
  gap: 6px;
  background: rgba(220, 38, 38, 0.85);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #fff;
`;
const ModalFlagNotif = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ff3b30;
  border: 1.5px solid #0d0018;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Mendl", sans-serif;
  font-size: 8px;
  font-weight: 700;
  color: #fff;
  line-height: 1;
  z-index: 5;
`;
const ModalBodyPill = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 4;
  height: 28px;
  border-radius: 100px;
  padding: 0 10px;
  background: rgba(16, 185, 129, 0.85);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #fff;
`;
const ModalCloseBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 10;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(12px);
  color: rgba(255, 255, 255, 0.85);
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;
const ModalBody = styled.div`
  padding: 20px 22px 48px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;
const ModalNameRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;
const ModalName = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: 32px;
  font-weight: 400;
  color: #fff;
  letter-spacing: 0.02em;
`;
const ModalAge = styled.span`
  font-family: "Mendl", sans-serif;
  font-size: 26px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.6);
`;
const ModalHandle = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.4);
  letter-spacing: 0.02em;
  margin-top: -12px;
`;
const ModalDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.1),
    transparent
  );
`;
const ModalSectionLabel = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 10px;
  font-weight: 500;
  color: rgba(168, 85, 253, 0.8);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 8px;
`;
const ModalBio = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.7;
`;
const ChipRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;
const Chip = styled.div`
  height: 30px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.07);
  backdrop-filter: blur(8px);
  padding: 0 14px;
  display: flex;
  align-items: center;
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
`;
const MessageBtn = styled.button`
  width: 100%;
  height: 50px;
  border-radius: 100px;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #7404be 0%, #a855fd 100%);
  box-shadow:
    0 4px 20px rgba(116, 4, 190, 0.5),
    0 1px 0 rgba(255, 255, 255, 0.2) inset;
  font-family: "Mendl", sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #fff;
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease;
  &:active {
    transform: scale(0.97);
  }
`;

function ProfileModal({ profile, onClose }) {
  const [closing, setClosing] = useState(false);
  function handleClose() {
    setClosing(true);
    setTimeout(onClose, 340);
  }
  return (
    <>
      <ModalBackdrop onClick={handleClose} />
      <ModalSheet $closing={closing}>
        <ModalPhotoWrap>
          <ModalPhoto src={profile.img} alt={profile.name} draggable="false" />
          <ModalPhotoGrad />
          <ModalFlagWrap>
            <ModalFlagPill>
              <FaFlag style={{ fontSize: 11 }} />
            </ModalFlagPill>
            {profile.notif && <ModalFlagNotif>{profile.notif}</ModalFlagNotif>}
          </ModalFlagWrap>
          <ModalBodyPill>Body count: {profile.bodyCount}</ModalBodyPill>
        </ModalPhotoWrap>

        {/* Close sits outside the photo wrap, top-right of sheet */}
        <div style={{ position: "relative" }}>
          <ModalCloseBtn
            onClick={handleClose}
            style={{ position: "absolute", top: -200, right: 16 }}
          >
            ✕
          </ModalCloseBtn>
        </div>

        <ModalBody>
          <div>
            <ModalNameRow>
              <ModalName>{profile.name}</ModalName>
              <ModalAge>{profile.age}</ModalAge>
            </ModalNameRow>
            <ModalHandle>@{profile.handle}</ModalHandle>
          </div>

          <MessageBtn>
            <IconMsg /> Send a Message
          </MessageBtn>

          <ModalDivider />

          <div>
            <ModalSectionLabel>About</ModalSectionLabel>
            <ModalBio>{profile.bio}</ModalBio>
          </div>

          <ModalDivider />

          <div>
            <ModalSectionLabel>Looking for</ModalSectionLabel>
            <ChipRow>
              {profile.tags.map((t) => (
                <Chip key={t}>{t}</Chip>
              ))}
            </ChipRow>
          </div>

          <ModalDivider />

          <div>
            <ModalSectionLabel>Hobbies</ModalSectionLabel>
            <ChipRow>
              {profile.hobbies.map((h) => (
                <Chip key={h}>{h}</Chip>
              ))}
            </ChipRow>
          </div>

          <ModalDivider />

          <div>
            <ModalSectionLabel>Interests</ModalSectionLabel>
            <ChipRow>
              {profile.interests.map((i) => (
                <Chip key={i}>{i}</Chip>
              ))}
            </ChipRow>
          </div>
        </ModalBody>
      </ModalSheet>
    </>
  );
}

// ─── Bottom Nav ───────────────────────────────────────────────────────────────

const NavBar = styled.nav`
  position: relative;
  z-index: 10;
  width: 100%;
  padding: 10px 20px 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const NavPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  height: 64px;
  border-radius: 100px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(#3d2253, #1f0e2a);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.22),
    0 1.5px 0 1px rgba(255, 255, 255, 0.28) inset,
    0 -1px 0 1px rgba(0, 0, 0, 0.2) inset,
    0 8px 32px rgba(0, 0, 0, 0.45);
  &::before {
    content: "";
    position: absolute;
    top: 1px;
    left: 14%;
    right: 14%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.5),
      transparent
    );
    border-radius: 50%;
    pointer-events: none;
  }
`;
const NavItem = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  background: none;
  border: none;
  cursor: pointer;
  height: 100%;
  padding: 0 4px;
  font-family: "Mendl", sans-serif;
  font-size: 9.5px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? "#fff" : "rgba(255,255,255,0.4)")};
  letter-spacing: 0.04em;
  transition: color 0.18s ease;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  svg {
    font-size: 19px;
  }
  &:active {
    opacity: 0.7;
  }
`;
const NavActiveBg = styled.div`
  position: absolute;
  top: 50%;
  left: ${({ $index, $count }) =>
    `calc(${(($index + 0.5) / $count) * 100}% - 28px)`};
  transform: translateY(-50%);
  width: 56px;
  height: 46px;
  border-radius: 20px;
  background: rgba(168, 85, 253, 0.22);
  border: 1px solid rgba(168, 85, 253, 0.35);
  pointer-events: none;
  transition: left 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;
const NavDot = styled.div`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #a855fd;
  margin-top: -1px;
`;

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconMatches = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);
const IconFlare = () => (
  <svg viewBox="0 0 24 24" width="1.2em" height="1.2em" fill="currentColor">
    <path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5z" />
  </svg>
);
const IconMsg = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const IconUser = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconHelp = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// ─── Data ────────────────────────────────────────────────────────────────────

const MUTUAL_MATCHES = [
  {
    id: 301,
    name: "Noah",
    age: 28,
    handle: "noah.uk",
    img: p6,
    notif: null,
    bodyCount: 9,
    online: true,
    isNew: true,
    flared: false,
    tags: ["Hook-ups", "Friendships"],
    bio: "London lad with a soft spot for rainy Sundays and vinyl records. Probably overthinking right now.",
    hobbies: ["Music", "Cycling", "Gaming"],
    interests: ["Film", "Psychology", "Football"],
  },
];

const WHO_LIKED_YOU = [
  {
    id: 101,
    name: "Aisha",
    age: 27,
    handle: "aisha.ng",
    img: p5,
    notif: null,
    bodyCount: 4,
    online: true,
    isNew: true,
    flared: false,
    tags: ["Deep Talks", "Romance"],
    bio: "Lagos → London. I'm a documentary filmmaker who cries at beautiful cinematography. Let's talk about things that actually matter over jollof rice.",
    hobbies: ["Writing", "Dancing", "Gardening"],
    interests: ["Social Justice", "Literature", "Afrobeats"],
  },
  {
    id: 102,
    name: "Elena",
    age: 25,
    handle: "elena.gr",
    img: p7,
    notif: 1,
    bodyCount: 1,
    online: false,
    isNew: true,
    flared: false,
    tags: ["Relationships"],
    bio: "Greek sun in my veins, always chasing the next sunset. Architect in training, human forever.",
    hobbies: ["Sailing", "Drawing", "Cooking"],
    interests: ["Design", "History", "Nature"],
  },
  {
    id: 103,
    name: "Mia",
    age: 24,
    handle: "mia.paris",
    img: p2,
    notif: null,
    bodyCount: 2,
    online: true,
    isNew: false,
    flared: true,
    tags: ["Relationships", "Romance"],
    bio: "Parisian at heart, wherever I am. I spend my weekends at art galleries and my evenings reading novels I'll never finish. Looking for something real.",
    hobbies: ["Painting", "Reading", "Ballet", "Café hopping"],
    interests: ["Art", "Literature", "Fashion", "Cinema"],
  },
];

const PEOPLE_I_LIKED = [
  {
    id: 201,
    name: "Amanda",
    age: 21,
    handle: "amanda.vita",
    img: p4,
    notif: 2,
    bodyCount: 3,
    online: false,
    isNew: false,
    flared: false,
    tags: ["Hook-ups", "Relationships"],
    bio: "Living for sunsets, espresso, and spontaneous road trips. I believe the best conversations happen at 2am. Half Italian, whole trouble.",
    hobbies: ["Hiking", "Vintage Shopping", "Film Photography", "Pasta Making"],
    interests: ["Travel", "Astrology", "True Crime", "Indie Music"],
  },
  {
    id: 202,
    name: "Lucas",
    age: 29,
    handle: "lucas.br",
    img: p1,
    notif: null,
    bodyCount: 7,
    online: true,
    isNew: false,
    flared: false,
    tags: ["Casual", "Friendships"],
    bio: "Software engineer by day, amateur chef by night. Fluent in sarcasm and four programming languages. Looking for someone to debate pizza toppings with.",
    hobbies: ["Cooking", "Gym", "Chess", "Skateboarding"],
    interests: ["Tech", "Football", "Stand-up Comedy", "Coffee"],
  },
  {
    id: 203,
    name: "Richard",
    age: 31,
    handle: "rich.nyc",
    img: p3,
    notif: 2,
    bodyCount: 11,
    online: false,
    isNew: false,
    flared: true,
    tags: ["Hook-ups", "Casual"],
    bio: "NYC-based architect with an unhealthy obsession with exposed brick and good bourbon. I'll charm you with dad jokes and cook you brunch.",
    hobbies: ["Architecture", "Bourbon Tasting", "Running", "Jazz"],
    interests: ["Design", "History", "Sports", "Food"],
  },
];

// ─── Match Card Component ─────────────────────────────────────────────────────

function PersonCard({ person, onClick }) {
  return (
    <MatchCard onClick={() => onClick(person)}>
      <MatchImg src={person.img} alt={person.name} draggable="false" />
      <MatchGrad />
      {person.isNew && !person.flared && <NewBadge>New</NewBadge>}
      {person.flared && <FlareBadge>🔥 Flared</FlareBadge>}
      {person.online && <OnlineDot />}
      <MatchInfo>
        <MatchName>
          {person.name}
          <MatchAge>{person.age}</MatchAge>
        </MatchName>
        <MatchHandle>@{person.handle}</MatchHandle>
      </MatchInfo>
    </MatchCard>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

// ─── Flare Popup ──────────────────────────────────────────────────────────────

const FlareBackdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: 60;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(14px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  animation: ${fadeIn} 0.3s ease both;
`;
const FlareSheet = styled.div`
  width: 100%;
  background: linear-gradient(170deg, #1c1200 0%, #0e0900 55%, #130b00 100%);
  border-radius: 24px;
  border: 1px solid rgba(212, 160, 23, 0.5);
  padding: 36px 28px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  animation:
    ${scaleIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1) both,
    ${goldGlow} 3s ease-in-out 0.4s infinite;
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 24px;
    pointer-events: none;
    background: radial-gradient(
      ellipse 80% 45% at 50% 0%,
      rgba(212, 160, 23, 0.14) 0%,
      transparent 70%
    );
  }
`;
const FlareIconWrap = styled.div`
  font-size: 56px;
  margin-bottom: 16px;
  animation: ${flareIconPulse} 2s ease-in-out infinite;
  line-height: 1;
`;
const FlareTitle = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: 22px;
  font-weight: 600;
  font-style: italic;
  letter-spacing: 0.02em;
  margin-bottom: 14px;
  background: linear-gradient(135deg, #f5d060, #c89010);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
  line-height: 1.3;
`;
const FlareBody = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: rgba(240, 210, 140, 0.8);
  text-align: center;
  line-height: 1.7;
  margin-bottom: 28px;
  max-width: 300px;
`;
const FlareHighlight = styled.span`
  color: #f5c842;
  font-weight: 600;
  -webkit-text-fill-color: #f5c842;
`;
const FlareBtnRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;
const FlareNoBtn = styled.button`
  flex: 1;
  height: 50px;
  border-radius: 100px;
  cursor: pointer;
  border: 1px solid rgba(212, 160, 23, 0.28);
  background: rgba(212, 160, 23, 0.07);
  backdrop-filter: blur(8px);
  font-family: "Mendl", sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: rgba(212, 160, 23, 0.55);
  letter-spacing: 0.02em;
  transition: background 0.15s ease;
  &:active {
    background: rgba(212, 160, 23, 0.14);
  }
`;
const FlareYesBtn = styled.button`
  flex: 2;
  height: 50px;
  border-radius: 100px;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #e8a800 0%, #c47d00 50%, #a06000 100%);
  box-shadow:
    0 6px 24px rgba(212, 160, 23, 0.45),
    0 1px 0 rgba(255, 220, 100, 0.3) inset;
  font-family: "Mendl", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #fff3cc;
  letter-spacing: 0.04em;
  transition: transform 0.15s ease;
  &:active {
    transform: scale(0.97);
  }
`;

export default function MatchesPage({ onNavigate }) {
  const [tab, setTab] = useState("matches");
  const [viewingProfile, setViewingProfile] = useState(null);
  const [showFlarePopup, setShowFlarePopup] = useState(true);

  const TABS = [
    { id: "matches", label: `Matches (${MUTUAL_MATCHES.length})` },
    { id: "liked-you", label: `Who Liked You (${WHO_LIKED_YOU.length})` },
    { id: "i-liked", label: `I Liked (${PEOPLE_I_LIKED.length})` },
  ];

  function renderEmpty(msg) {
    return (
      <EmptyWrap>
        <EmptyCircle>💜</EmptyCircle>
        <EmptyTitle>Nothing here yet</EmptyTitle>
        <EmptySub>{msg}</EmptySub>
      </EmptyWrap>
    );
  }

  function renderGrid(people, emptyMsg) {
    if (!people.length) return renderEmpty(emptyMsg);
    return (
      <Grid>
        {people.map((p) => (
          <PersonCard key={p.id} person={p} onClick={setViewingProfile} />
        ))}
      </Grid>
    );
  }

  const newLikes = WHO_LIKED_YOU.filter((p) => p.isNew);
  const earlierLikes = WHO_LIKED_YOU.filter((p) => !p.isNew);

  return (
    <>
      <GlobalStyle />
      <Screen>
        {/* ── Flare Popup ── */}
        {showFlarePopup && (
          <FlareBackdrop>
            <FlareSheet onClick={(e) => e.stopPropagation()}>
              <FlareIconWrap>🔥</FlareIconWrap>
              <FlareTitle>Someone Just Sent You a Flare!</FlareTitle>
              <FlareBody>
                Flares are{" "}
                <FlareHighlight>rare and very special</FlareHighlight> — it
                means they want to Link Up with you today!
                <br />
                <br />
                Do you want to see what the person who sent the Flare looks
                like? You might just find your next steamy partner if you do! 😉
              </FlareBody>
              <FlareBtnRow>
                <FlareNoBtn onClick={() => setShowFlarePopup(false)}>
                  No
                </FlareNoBtn>
                <FlareYesBtn
                  onClick={() => {
                    setShowFlarePopup(false);
                    setTab("liked-you");
                  }}
                >
                  Yes 🔥
                </FlareYesBtn>
              </FlareBtnRow>
            </FlareSheet>
          </FlareBackdrop>
        )}

        {/* Profile modal */}
        {viewingProfile && (
          <ProfileModal
            profile={viewingProfile}
            onClose={() => setViewingProfile(null)}
          />
        )}

        {/* Top bar */}
        <TopBar>
          <LogoImg src={logo} alt="" />
          <TopTitle>Matches</TopTitle>
          <TopRight />
        </TopBar>

        {/* Tabs */}
        <TabWrap>
          {TABS.map((t) => (
            <TabBtn
              key={t.id}
              $active={tab === t.id}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </TabBtn>
          ))}
        </TabWrap>

        {/* Body */}
        <Body>
          {/* ── Matches tab ── */}
          {tab === "matches" && (
            <>
              {MUTUAL_MATCHES.length > 0
                ? renderGrid(MUTUAL_MATCHES, "")
                : renderEmpty(
                    "Keep swiping on the Discover page to find your perfect match!",
                  )}
            </>
          )}

          {/* ── Who Liked You tab ── */}
          {tab === "liked-you" && (
            <>
              {newLikes.length > 0 && (
                <>
                  <SectionLabel>New</SectionLabel>
                  {renderGrid(newLikes, "")}
                  {earlierLikes.length > 0 && (
                    <>
                      <SectionGap />
                      <Divider />
                    </>
                  )}
                </>
              )}
              {earlierLikes.length > 0 && (
                <>
                  <SectionLabel>Earlier</SectionLabel>
                  {renderGrid(earlierLikes, "")}
                </>
              )}
              {WHO_LIKED_YOU.length === 0 &&
                renderEmpty("No one has liked you yet — keep going!")}
            </>
          )}

          {/* ── I Liked tab ── */}
          {tab === "i-liked" && (
            <>
              {renderGrid(
                PEOPLE_I_LIKED,
                "You haven't liked anyone yet — start swiping!",
              )}
            </>
          )}
        </Body>

        {/* Bottom nav */}
        <NavBar>
          <NavPill>
            <NavActiveBg $index={0} $count={5} />
            <NavItem $active onClick={() => onNavigate?.("matches")}>
              <IconMatches />
              <span>Matches</span>
              <NavDot />
            </NavItem>
            <NavItem onClick={() => onNavigate?.("discover")}>
              <IconFlare />
              <span>Discover</span>
            </NavItem>
            <NavItem onClick={() => onNavigate?.("messages")}>
              <IconMsg />
              <span>Messages</span>
            </NavItem>
            <NavItem onClick={() => onNavigate?.("profile")}>
              <IconUser />
              <span>Profile</span>
            </NavItem>
            <NavItem onClick={() => onNavigate?.("help")}>
              <IconHelp />
              <span>Help</span>
            </NavItem>
          </NavPill>
        </NavBar>
      </Screen>
    </>
  );
}
